import React, { useState } from 'react';
import { useBrandingColors } from '../../hooks/useBrandingColors';

export interface TabOption<T extends string = string> {
  id: T;
  label: string;
}

interface ContentTabToggleProps<T extends string = string> {
  options: TabOption<T>[];
  activeTab: T;
  onTabChange: (tabId: T) => void;
  className?: string;
}

const hexToRgba = (hex: string, alpha: number): string => {
  const m = hex.replace('#', '');
  const r = parseInt(m.length === 3 ? m[0] + m[0] : m.slice(0, 2), 16);
  const g = parseInt(m.length === 3 ? m[1] + m[1] : m.slice(2, 4), 16);
  const b = parseInt(m.length === 3 ? m[2] + m[2] : m.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const ContentTabToggle = <T extends string = string>({
  options,
  activeTab,
  onTabChange,
  className = "",
}: ContentTabToggleProps<T>): React.ReactElement => {
  const colors = useBrandingColors();
  const [hoveredId, setHoveredId] = useState<T | null>(null);
  return (
    <div className={`bg-transparent relative rounded-[16px] shrink-0 ${className}`}>
      <div className="box-border content-stretch flex flex-row gap-1 items-start justify-start overflow-clip relative rounded-[16px]" style={{ padding: '2px' }}>
        {options.map((option) => {
          const isActive = activeTab === option.id;
          const isHover = hoveredId === option.id && !isActive;
          return (
            <div
              key={option.id}
              onClick={() => onTabChange(option.id)}
              onMouseEnter={() => setHoveredId(option.id)}
              onMouseLeave={() => setHoveredId(null)}
              className={`${
                isActive
                  ? "bg-white shadow-[0px_6px_10px_0px_rgba(27,28,29,0.06),0px_2px_4px_0px_rgba(27,28,29,0.02)]"
                  : ""
              } box-border content-stretch flex flex-row gap-1.5 items-center justify-center overflow-clip py-2 px-3 relative rounded-[14px] shrink-0 cursor-pointer transition-all duration-200 flex-1`}
              style={!isActive && isHover ? { backgroundColor: hexToRgba(colors.primary, 0.08) } : undefined}
            >
              <div
                className="font-inter font-medium leading-[0] not-italic relative shrink-0 text-[14px] text-left text-nowrap transition-colors duration-200"
                style={{
                  color: isActive
                    ? colors.primary
                    : isHover
                      ? colors.primary
                      : '#868C98',
                }}
              >
                <p className="adjustLetterSpacing block leading-[20px] whitespace-pre">
                  {option.label}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <div
        aria-hidden="true"
        className="absolute border border-[#CDD0D5] border-solid inset-0 pointer-events-none rounded-[16px]"
      />
    </div>
  );
};